/* Adapted from the captured source video-reels-slider controller. */
if (!customElements.get('kellermont-video-reels')) {
  customElements.define('kellermont-video-reels', class extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.eventOptions = { signal: this.abortController.signal };
      this.videos = [...this.querySelectorAll('.reel-video')];
      this.visibleVideos = new Set();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.initSwiper();
      this.initObserver();
      this.initSoundToggles();
      this.initModalEvents();
      this.onVisibility = () => {
        if (document.hidden) this.stopAll();
        else this.resumeVisible();
      };
      document.addEventListener('visibilitychange', this.onVisibility, this.eventOptions);
      window.addEventListener('pagehide', () => this.stopAll(), this.eventOptions);
      window.addEventListener('pageshow', () => { this.stopAll(); this.resumeVisible(); }, this.eventOptions);
      this.dataset.ready = 'true';
    }
    initSwiper() {
      this.swiperContainer = this.querySelector('.swiper');
      if (!this.swiperContainer || !window.Swiper) return;
      this.swiperInstance = new window.Swiper(this.swiperContainer, {
        slidesPerView: 3.1,
        spaceBetween: 8,
        grabCursor: true,
        breakpoints: { 1024: { slidesPerView: 3.6, spaceBetween: 10 } }
      });
    }
    setMuted(video, muted) {
      if (video.muted !== muted) video.muted = muted;
      const button = video.closest('.reel-video-container')?.querySelector('.reel-mute');
      if (!button) return;
      button.classList.toggle('is-unmuted', !muted);
      button.setAttribute('aria-pressed', String(muted));
      button.setAttribute('aria-label', muted ? this.dataset.unmuteLabel : this.dataset.muteLabel);
    }
    initObserver() {
      this.videos.forEach(video => {
        video.defaultMuted = true;
        this.setMuted(video, true);
        video.addEventListener('playing', () => {
          video.parentElement.querySelector('.reel-poster')?.classList.add('is-hidden');
        }, this.eventOptions);
        video.addEventListener('volumechange', () => this.setMuted(video, video.muted), this.eventOptions);
      });
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this.visibleVideos.add(video);
            if (!document.hidden && !this.reducedMotion.matches) video.play().catch(() => {});
          } else {
            this.visibleVideos.delete(video);
            video.pause();
            this.setMuted(video, true);
          }
        });
      }, { threshold: 0.5 });
      this.videos.forEach(video => this.observer.observe(video));
    }
    toggleSound(container) {
      const video = container.querySelector('.reel-video');
      if (!video) return;
      const willUnmute = video.muted;
      if (willUnmute) this.videos.filter(item => item !== video).forEach(item => this.setMuted(item, true));
      this.setMuted(video, !willUnmute);
      video.play().catch(() => {});
    }
    initSoundToggles() {
      this.querySelectorAll('.reel-video-container').forEach(container => {
        container.addEventListener('click', () => {
          if (this.swiperInstance?.allowClick === false) return;
          this.toggleSound(container);
        }, this.eventOptions);
        const button = container.querySelector('.reel-mute');
        if (!button) return;
        button.disabled = false;
        button.addEventListener('click', event => {
          event.stopPropagation();
          this.toggleSound(container);
        }, this.eventOptions);
      });
    }
    initModalEvents() {
      // Retained source markup has no opening trigger: source cards toggle sound.
      // Keeping the source's inactive modal must not add image/video zoom behavior.
      this.modal = this.querySelector('.reel-video-modal');
      this.modalPlayer = this.querySelector('.reel-modal-player');
      if (!this.modal || !this.modalPlayer) return;
      const closeModal = () => {
        this.modal.classList.remove('is-active');
        this.modal.setAttribute('aria-hidden', 'true');
        this.modalPlayer.pause();
        this.modalPlayer.muted = true;
        this.modalPlayer.removeAttribute('src');
        this.modalPlayer.replaceChildren();
        this.modalPlayer.load();
      };
      this.querySelector('.reel-video-modal-close')?.addEventListener('click', closeModal, this.eventOptions);
      this.querySelector('.reel-video-modal-overlay')?.addEventListener('click', closeModal, this.eventOptions);
    }
    stopAll() {
      this.videos.forEach(video => { video.pause(); this.setMuted(video, true); });
      this.modalPlayer?.pause();
      if (this.modalPlayer) this.modalPlayer.muted = true;
    }
    resumeVisible() {
      if (document.hidden || this.reducedMotion.matches) return;
      this.visibleVideos.forEach(video => { this.setMuted(video, true); video.play().catch(() => {}); });
    }
    disconnectedCallback() {
      this.stopAll();
      this.observer?.disconnect();
      this.swiperInstance?.destroy();
      this.abortController?.abort();
      this.abortController = null;
      delete this.dataset.ready;
    }
  });
}
